import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { getToday, decideRecommendation } from "@/lib/today.functions";
import { getAdminDashboardStats } from "@/lib/dashboard.functions";
import { percentChange } from "@/lib/today-logic";
import type { TodayData, TodayMetric } from "@/lib/today.server";

export const Route = createFileRoute("/admin/")({
  head: () => ({ meta: [{ title: "Bugün · Yönetim" }, { name: "robots", content: "noindex, nofollow" }] }),
  validateSearch: z.object({ range: z.enum(["7d", "30d", "90d"]).optional() }),
  component: Today,
});

const RANGES = [
  { v: "7d", l: "7 gün" },
  { v: "30d", l: "30 gün" },
  { v: "90d", l: "90 gün" },
] as const;

const when = (iso: string) =>
  new Date(iso).toLocaleString("tr-TR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" });

function Today() {
  const range = Route.useSearch().range ?? "30d";
  const fn = useServerFn(getToday);
  const statsFn = useServerFn(getAdminDashboardStats);
  const { data, isLoading, error } = useQuery({ queryKey: ["admin-today", range], queryFn: () => fn({ data: { range } }) });
  const { data: live } = useQuery({ queryKey: ["admin-dashboard-stats"], queryFn: () => statsFn(), refetchInterval: 60_000 });
  const today = new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Istanbul" });

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink/60">{today}</div>
          <h1 className="font-serif text-4xl mt-1">Bugün</h1>
        </div>
        <div className="flex gap-4 text-sm" role="group" aria-label="Dönem">
          {RANGES.map((r) => (
            <Link key={r.v} to="/admin" search={{ range: r.v }} className={r.v === range ? "border-b border-ink" : "text-ink/55 hover:text-ink"}>
              {r.l}
            </Link>
          ))}
        </div>
      </div>

      {error ? (
        <p className="text-sm text-clay">Veriler alınamadı: {(error as Error).message}</p>
      ) : isLoading || !data ? (
        <p className="text-sm text-ink/60">Yükleniyor…</p>
      ) : (
        <>
          {data.coverageNote && (
            <p role="note" className="text-sm border-l-2 border-clay pl-3 text-ink/75">{data.coverageNote}</p>
          )}
          <Recommendations data={data} />
          <Metrics data={data} />
          <div className="grid md:grid-cols-2 gap-10">
            <TopPosts data={data} />
            <Sources data={data} live={live} />
          </div>
          <p className="text-[11px] text-ink/50">
            Hesaplama: {when(data.generatedAt)} · {data.periodLabel}.{" "}
            {data.comparable ? "Önceki eşdeğer dönemle karşılaştırılıyor." : "Ölçüm geçmişi önceki dönemi kapsamadığı için değişim oranı gösterilmiyor."}
          </p>
        </>
      )}
    </div>
  );
}

function Recommendations({ data }: { data: TodayData }) {
  const decide = useServerFn(decideRecommendation);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const act = async (key: string, basis: string, decision: "accept" | "snooze" | "dismiss" | "revert") => {
    setBusy(key);
    setMsg(null);
    try {
      await decide({ data: { key, basis, decision } });
      setMsg(decision === "accept" ? "Kabul edildi." : decision === "revert" ? "Karar geri alındı; öneri yeniden gösteriliyor." : decision === "snooze" ? "7 gün ertelendi." : "Yok sayıldı; dayanak değişmedikçe tekrar gösterilmeyecek.");
      await qc.invalidateQueries({ queryKey: ["admin-today"] });
    } catch (e) {
      setMsg((e as Error).message || "Karar kaydedilemedi.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby="oneriler">
      <h2 id="oneriler" className="font-serif text-2xl">Öncelikli öneriler</h2>
      <p className="text-sm text-ink/60 mb-3">Her öneri kayıtlı bir veriye dayanır. Siz karar vermeden hiçbir şey değişmez.</p>
      {msg && <p role="status" className="text-xs text-moss mb-2">{msg}</p>}
      {data.recommendations.length === 0 ? (
        <p className="text-sm text-ink/60 border-t border-border py-4">Şu an veriye dayalı bekleyen öneri yok.</p>
      ) : (
        <ol className="border-t border-border">
          {data.recommendations.map((r, i) => (
            <li key={r.key} className="grid gap-x-5 gap-y-2 py-4 border-b border-border md:grid-cols-[24px_1fr_240px_auto]">
              <span className="font-serif text-xl text-clay hidden md:block">{i + 1}</span>
              <div>
                <div className="font-serif text-lg leading-snug">{r.title}</div>
                <div className="text-sm text-ink/65">{r.reason}</div>
              </div>
              <div className="text-[11px] text-ink/55 leading-relaxed">
                Dayanak: {r.evidence}
                <br />
                Kaynak: {r.source} · {when(r.measuredAt)}
              </div>
              <div className="flex flex-wrap items-start gap-2 text-xs">
                <Link
                  to={r.link.to}
                  params={r.link.params as never}
                  onClick={() => void act(r.key, r.basis, "accept")}
                  className="px-3 py-1.5 rounded-full bg-ink text-paper"
                >
                  {r.link.label}
                </Link>
                <button disabled={busy === r.key} onClick={() => act(r.key, r.basis, "snooze")} className="px-3 py-1.5 rounded-full border border-ink/40 disabled:opacity-50">
                  Ertele
                </button>
                <button disabled={busy === r.key} onClick={() => act(r.key, r.basis, "dismiss")} className="px-2 py-1.5 text-ink/60 hover:text-ink disabled:opacity-50">
                  Yok say
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
      {data.reversible.length > 0 && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer select-none text-ink/70">Ertelenen ve yok sayılan öneriler ({data.reversible.length})</summary>
          <ul className="mt-2 border-t border-border">
            {data.reversible.map((d) => (
              <li key={d.key + d.basis} className="flex flex-wrap items-center justify-between gap-3 py-2 border-b border-border">
                <span>
                  <span className="font-serif">{d.title}</span>
                  <span className="block text-[11px] text-ink/55">
                    {d.decision === "snooze" && d.snoozeUntil ? `Ertelendi · ${when(d.snoozeUntil)} tarihine kadar` : "Yok sayıldı"} · karar: {when(d.decidedAt)}
                  </span>
                </span>
                <button disabled={busy === d.key} onClick={() => act(d.key, d.basis, "revert")} className="px-3 py-1.5 rounded-full border border-ink/40 text-xs disabled:opacity-50">
                  Geri al
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function Delta({ m, comparable }: { m: TodayMetric; comparable: boolean }) {
  if (!comparable || m.previous === null || m.value === null) return null;
  const pct = percentChange(m.value, m.previous);
  if (pct === null) return null;
  const sign = pct > 0 ? "+" : "";
  return <span className="text-[11px] text-ink/60 ml-2">{sign}{Math.round(pct * 100)}% önceki döneme göre</span>;
}

function Metrics({ data }: { data: TodayData }) {
  return (
    <section aria-label="Temel ölçümler" className="grid grid-cols-2 lg:grid-cols-3 border-t border-ink">
      {data.metrics.map((m) => (
        <div key={m.key} className="py-4 pr-4 border-b border-border lg:[&:nth-child(3n+2)]:pl-4 lg:[&:nth-child(3n)]:pl-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-ink/60">{m.label}</div>
          <div className="font-serif text-3xl mt-1">
            {m.display}
            <Delta m={m} comparable={data.comparable} />
          </div>
          <details className="mt-1 text-[11px] text-ink/55">
            <summary className="cursor-pointer select-none">Tanım ve kaynak</summary>
            <p className="mt-1">{m.definition}</p>
            <p>Kaynak: {m.source}</p>
          </details>
        </div>
      ))}
    </section>
  );
}

function TopPosts({ data }: { data: TodayData }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink/60 mb-2">En çok okunan yazılar · {data.periodLabel.toLowerCase()}</div>
      {data.topPosts.length === 0 ? (
        <p className="text-sm text-ink/60">Bu dönemde yazı okuması kaydı yok.</p>
      ) : (
        <ul>
          {data.topPosts.map((p) => (
            <li key={p.path} className="flex justify-between gap-4 py-2 border-t border-border">
              <a href={p.path} target="_blank" rel="noreferrer" className="font-serif hover:text-clay truncate">{p.title}</a>
              <span className="tabular-nums shrink-0">{p.views}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink/60 mt-6 mb-2">Yükselen yazılar</div>
      {!data.comparable ? (
        <p className="text-sm text-ink/60">Önceki dönem tam ölçülmediği için yükselen yazı hesaplanmıyor.</p>
      ) : data.rising.length === 0 ? (
        <p className="text-sm text-ink/60">Okunması önceki döneme göre belirgin artan yazı yok.</p>
      ) : (
        <ul>
          {data.rising.map((p) => (
            <li key={p.path} className="flex justify-between gap-4 py-2 border-t border-border">
              <span className="font-serif truncate">{p.title}</span>
              <span className="tabular-nums shrink-0 text-ink/70">{p.previousViews} → {p.views}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Sources({ data, live }: { data: TodayData; live?: { activeVisitors: number; totalDownloads: number; downloadsLast7d: number; memberCount: number } }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink/60 mb-2">Veri kaynakları ve sistem</div>
      <ul className="text-sm">
        {data.connections.map((c) => (
          <li key={c.name} className="py-2 border-t border-border">
            <b className="font-medium">{c.name}</b>{" "}
            <span className={c.status === "connected" ? "text-moss" : "text-ink/60"}>— {c.note}</span>
          </li>
        ))}
        <li className="py-2 border-t border-border">
          <b className="font-medium">Erişim denetimi</b>{" "}
          {data.audit ? (
            <span className={data.audit.ok ? "text-moss" : "text-clay"}>
              — {data.audit.ok ? "temiz" : `${data.audit.findings} bulgu`} · {when(data.audit.ranAt)}
            </span>
          ) : (
            <span className="text-ink/60">— henüz çalışmadı</span>
          )}
        </li>
        {live && (
          <li className="py-2 border-t border-border text-ink/70">
            Şu an sitede {live.activeVisitors} okur · {live.memberCount} giriş hesabı · kitap indirme {live.totalDownloads} (son 7 gün {live.downloadsLast7d})
          </li>
        )}
      </ul>
    </section>
  );
}
